import { useEffect, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { cmsApi } from '../lib/api/cms';

const AboutSection = () => {
  const { t, getTranslation } = useTranslation();
  const [aboutImage, setAboutImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Get dynamic content from CMS
  const subtitle = t('about.subtitle') || 'Forestry OÜ on tegutsenud Eesti metsandussektoris aastast 2005, pakkudes klientidele kvaliteetseid teenuseid ja usaldusväärseid lahendusi kogu riigi territooriumil.';
  const yearsExperience = t('about.yearsExperience') || 'Usaldusväärne partner Eesti metsandusvaldkonnas juba 20 aastat';
  const founded = t('about.companyInfo.founded') || '2005';
  const address = t('about.companyInfo.address') || 'Osmussaare tänav 8, 13811 Tallinn, Eesti';
  const phone1 = t('about.companyInfo.phone1') || '+372 6707 110';
  const phone2 = t('about.companyInfo.phone2') || '+372 5663 6076';
  const email = t('about.companyInfo.email') || 'info@forestryest.ee';

  // Get team members from translations or use defaults
  const teamMembers = getTranslation('about.teamMembers') || [
    {
      name: "Ain Meister",
      position: "Juhatuse liige",
      phone: "+372 5663 6076",
      email: "ain@forestryest.ee"
    },
    {
      name: "Risto Esko",
      position: "Juhatuse liige", 
      phone: "+372 5663 8551",
      email: "risto@forestryest.ee"
    }
  ];

  // Default fallback image
  const defaultImage = "https://images.pexels.com/photos/2933265/pexels-photo-2933265.jpeg?auto=compress&cs=tinysrgb&w=1200";

  useEffect(() => {
    loadAboutImage();
  }, []);

  const loadAboutImage = async () => {
    try {
      const { data: aboutSection, error } = await cmsApi.getSection('about');
      
      if (error) {
        console.error('Error loading about section:', error);
        setAboutImage(defaultImage);
        return;
      }

      // Use uploaded image if available, otherwise use default
      setAboutImage(aboutSection?.background_image || defaultImage);
    } catch (err) {
      console.error('Error loading about image:', err);
      setAboutImage(defaultImage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });

    document.querySelectorAll('.section-fade-in').forEach((element) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  // Listen for CMS updates
  useEffect(() => {
    const handleCMSUpdate = () => {
      loadAboutImage();
    };

    window.addEventListener('translationsUpdated', handleCMSUpdate);
    return () => {
      window.removeEventListener('translationsUpdated', handleCMSUpdate);
    };
  }, []);

  return (
    <section id="about" className="py-20 bg-gray-50 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-forest-pattern opacity-10"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
            {t('about.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Company image */}
          <div className="rounded-xl overflow-hidden shadow-xl relative group">
            {loading ? (
              <div className="w-full h-64 bg-gray-200 animate-pulse flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700"></div>
              </div>
            ) : (
              <img 
                src={aboutImage || defaultImage}
                alt="Forestry OÜ meeskond" 
                className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
                onError={(e) => {
                  // Fallback to default image if CMS image fails to load
                  const target = e.target as HTMLImageElement;
                  if (target.src !== defaultImage) {
                    target.src = defaultImage;
                  }
                }}
              />
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary-900/90 to-transparent p-6">
              <p className="text-white text-lg font-medium">
                {yearsExperience}
              </p>
            </div>
          </div>

          {/* Company info */}
          <div className="bg-white rounded-xl shadow-md p-8 section-fade-in">
            <h3 className="text-2xl font-heading font-bold text-primary-800 mb-6">
              {t('about.companyOverview')}
            </h3>
            
            <div className="space-y-6">
              <div className="flex border-b border-gray-100 pb-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-full mr-4">
                  <svg className="w-5 h-5 text-primary-700" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4zm3 1h6v4H7V5zm8 8v2h1v1H4v-1h1v-2h-.5a.5.5 0 01-.5-.5v-2a.5.5 0 01.5-.5H7v-1h6v1h2.5a.5.5 0 01.5.5v2a.5.5 0 01-.5.5H15z" clipRule="evenodd"></path>
                  </svg>
                </div>
                <div>
                  <h4 className="text-gray-900 font-medium mb-1">{t('about.founded')}</h4>
                  <p className="text-gray-600">{founded}</p>
                </div>
              </div>
              
              <div className="flex border-b border-gray-100 pb-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-full mr-4">
                  <svg className="w-5 h-5 text-primary-700" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"></path>
                  </svg>
                </div>
                <div>
                  <h4 className="text-gray-900 font-medium mb-1">{t('about.location')}</h4>
                  <p className="text-gray-600">
                    <a href="#contact">{address}</a>
                  </p>
                </div>
              </div>
              
              <div className="flex border-b border-gray-100 pb-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-full mr-4">
                  <svg className="w-5 h-5 text-primary-700" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path>
                  </svg>
                </div>
                <div>
                  <h4 className="text-gray-900 font-medium mb-1">{t('about.phone')}</h4>
                  <a href={`tel:${phone1}`} className="text-gray-600 hover:text-primary-800 transition-colors">{phone1}</a><br></br>
                  <a href={`tel:${phone2}`} className="text-gray-600 hover:text-primary-800 transition-colors">{phone2}</a>
                </div>
              </div>
              
              <div className="flex pb-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-full mr-4">
                  <svg className="w-5 h-5 text-primary-700" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                  </svg>
                </div>
                <div>
                  <h4 className="text-gray-900 font-medium mb-1">{t('about.email')}</h4>
                  <p className="text-gray-600">
                    <a href={`mailto:${email}`} className="text-gray-600 hover:text-primary-800 transition-colors">
                      {email}
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-heading font-bold text-primary-800 mt-8 mb-6">
              {t('about.boardMembers')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {teamMembers.map((manager: any, index: number) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 transition-all hover:shadow-md">
                  <h4 className="text-lg font-medium text-gray-900 mb-2">{manager.name}</h4>
                  <p className="text-gray-500 text-sm mb-3">{manager.position}</p>
                  <div className="flex items-center mb-2">
                    <svg className="w-4 h-4 text-primary-700 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path>
                    </svg>
                    <a href={`tel:${manager.phone}`} className="text-gray-600 hover:text-primary-800 transition-colors">{manager.phone}</a>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-primary-700 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                    </svg>
                    <a href={`mailto:${manager.email}`} className="text-gray-600 hover:text-primary-800 transition-colors">
                      {manager.email}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;